import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import {
  Activity,
  ChevronRight,
  Database,
  ExternalLink,
  Info,
  LogOut,
  RefreshCw,
  User,
} from 'lucide-react';

import { useLogoutMutation } from '../redux/services/user/userApi';
import {
  selectIsAdmin,
  selectUserInfo,
  selectUserInfoError,
  selectUserToken,
} from '../redux/features/user/userSelectors';
import { useGetBackendVersionQuery } from '../redux/services/version/versionApi';
import { useGetQueueDashboardQuery } from '../features/queue-dashboard/api/queueDashboardApi';

import { deleteCookie } from '../utils/cookies';
import { Container } from '../layouts/Container';
import { errorMessages, APP_VERSION } from '../data/constants';
import { QueueDashboardModal } from '../features/queue-dashboard/QueueDashboardModal';
import {
  Avatar,
  getInitials,
  PasswordForm,
  ProfileForm,
} from '../features/profile-settings/ProfileSettings';

// ---------- small presentational primitives --------------------------------

const Card = ({ children, className = '' }) => (
  <div
    className={`rounded-lg border border-gray-200 bg-white p-5 hover:shadow-sm transition-all ${className}`}
  >
    {children}
  </div>
);

const SkeletonLine = ({ className = 'h-4 w-32' }) => (
  <span
    className={`inline-block rounded bg-lightGrayColor animate-pulse ${className}`}
  />
);

const SHA_PATTERN = /^[a-f0-9]{7,40}$/i;

const VersionPill = ({ children, repo }) => {
  const value = typeof children === 'string' ? children : '';
  const isSha = SHA_PATTERN.test(value);

  if (isSha && repo) {
    return (
      <a
        href={`https://github.com/sudoers-group/${repo}/commit/${value}`}
        target="_blank"
        rel="noopener noreferrer"
        title={`Open ${repo}@${value} on GitHub`}
        className="inline-flex items-center gap-1 bg-lightGrayColor hover:bg-purpleColor/10 px-2 py-0.5 rounded text-xs font-mono text-blackColor hover:text-purpleColor transition-colors"
      >
        {value}
        <ExternalLink
          size={10}
          className="opacity-60"
        />
      </a>
    );
  }

  return (
    <span className="bg-lightGrayColor px-2 py-0.5 rounded text-xs font-mono text-blackColor">
      {children}
    </span>
  );
};

// Row that acts as a navigation link inside a card
const NavRow = ({ icon: Icon, title, description, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group flex items-center gap-3 w-full text-left rounded py-3 px-2 -mx-2 hover:bg-lightestGrayColor transition-colors"
  >
    <span className="flex items-center justify-center w-9 h-9 rounded bg-purpleColor/10 text-purpleColor flex-shrink-0">
      <Icon size={18} />
    </span>
    <span className="flex flex-col leading-tight min-w-0 flex-1">
      <span className="text-sm font-medium text-blackColor truncate">
        {title}
      </span>
      <span className="text-xs text-darkGrayColor truncate">{description}</span>
    </span>
    <ChevronRight
      size={18}
      className="text-darkGrayColor group-hover:text-blackColor flex-shrink-0 transition-colors"
    />
  </button>
);

// ---------- background-jobs status dot -------------------------------------

/**
 * Reads /api/queue-dashboard live and returns a dot color + short stat string.
 * Envelope is `{ data: {...} }` per queueDashboardApi.
 */
const useQueueStatus = (token, isAdmin) => {
  const { data, isLoading, error } = useGetQueueDashboardQuery(
    { token },
    {
      skip: !token || !isAdmin,
      pollingInterval: 10000,
      skipPollingIfUnfocused: true,
    }
  );

  return useMemo(() => {
    if (!token || isLoading || error || !data?.data) {
      return {
        tone: 'gray',
        dotClass: 'bg-darkGrayColor/40',
        label: '—',
      };
    }

    const payload = data.data;
    const queues = payload.queues ?? [];
    const pending = queues.reduce((sum, q) => sum + (q?.pending ?? 0), 0);
    const reserved = queues.reduce((sum, q) => sum + (q?.reserved ?? 0), 0);
    const throughput1m = payload.throughput?.last_1m ?? 0;
    const failedTotal = payload.failed_jobs?.total ?? 0;

    let tone = 'green';
    let dotClass = 'bg-greenColor';

    if (failedTotal > 0) {
      tone = 'red';
      dotClass = 'bg-redColor';
    } else if (pending > 50) {
      tone = 'orange';
      dotClass = 'bg-orangeColor';
    } else if (reserved > 0 || pending > 0) {
      tone = 'blue';
      dotClass = 'bg-blueColor';
    }

    return {
      tone,
      dotClass,
      label: `${pending} queued · ${throughput1m}/min`,
    };
  }, [data, isLoading, error, token]);
};

// ---------- page -----------------------------------------------------------

export const Settings = () => {
  const [isUserLoading, setIsUserLoading] = useState(true);
  const [isQueueDashboardOpen, setIsQueueDashboardOpen] = useState(false);

  const [logout, { error: logoutError }] = useLogoutMutation();
  const userToken = useSelector(selectUserToken);
  const userInfo = useSelector(selectUserInfo);
  const userInfoError = useSelector(selectUserInfoError);
  const isAdmin = useSelector(selectIsAdmin);

  const {
    data: backendVersionData,
    isLoading: isBackendVersionLoading,
    error: backendVersionError,
  } = useGetBackendVersionQuery();

  const navigate = useNavigate();
  const queueStatus = useQueueStatus(userToken, isAdmin);

  const handleLogout = async () => {
    const response = await logout(userToken);

    // Always delete token and navigate, even if logout API fails
    deleteCookie('token');

    if (logoutError || response?.error) {
      console.error(
        logoutError
          ? `${logoutError.originalStatus} ${logoutError.status}`
          : errorMessages.wentWrong
      );
    }

    // Full reload drops every redux slice and the RTK Query cache, so the next
    // agent on this browser starts clean.
    window.location.assign('/login');
  };

  useEffect(() => {
    if (userInfo) {
      setIsUserLoading(false);
    }
  }, [userInfo]);

  const email = userInfo?.email;

  return (
    <Container>
      <div className="w-full max-w-3xl mx-auto flex flex-col gap-5 py-2">
        {/* Page header ------------------------------------------------- */}
        <header className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold text-blackColor">Settings</h1>
          <p className="text-sm text-darkGrayColor">
            {isAdmin
              ? 'Manage your account, integrations, and background jobs'
              : 'Manage your account and integrations'}
          </p>
        </header>

        {/* Account card ------------------------------------------------ */}
        {!userInfoError && (
          <Card>
            <div className="flex items-center gap-4">
              {isUserLoading ? (
                <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-purpleColor text-white flex-shrink-0">
                  <User size={18} />
                </div>
              ) : (
                <Avatar
                  src={userInfo?.image}
                  initials={getInitials(userInfo)}
                  className="w-10 h-10 md:w-12 md:h-12 text-sm"
                />
              )}

              <div className="flex flex-col leading-tight min-w-0 flex-1">
                <span className="text-xs uppercase tracking-wider text-darkGrayColor">
                  Signed in as
                </span>
                <span className="text-sm font-medium text-blackColor truncate mt-0.5">
                  {isUserLoading ? (
                    <SkeletonLine className="h-4 w-40" />
                  ) : (
                    email || '—'
                  )}
                </span>
              </div>

              <button
                type="button"
                onClick={handleLogout}
                data-testid="settings-logout-button"
                aria-label="Sign out"
                className="flex items-center justify-center gap-1.5 w-10 h-10 md:w-auto md:h-auto text-sm font-medium text-darkGrayColor border border-gray-200 rounded md:px-3 md:py-1.5 hover:border-redColor hover:text-redColor hover:bg-redColor/5 transition-colors flex-shrink-0"
              >
                <LogOut size={14} />
                <span className="hidden md:inline">Sign out</span>
              </button>
            </div>
          </Card>
        )}

        {userInfo && (
          <>
            <Card>
              <ProfileForm />
            </Card>
            <Card>
              <PasswordForm />
            </Card>
          </>
        )}

        {/* Parser & Import card --------------------------------------- */}
        <Card>
          <div className="flex flex-col gap-1">
            <h2 className="text-xs uppercase tracking-wider text-darkGrayColor font-medium px-2">
              Parser &amp; Import
            </h2>
            <div className="flex flex-col divide-y divide-gray-100">
              <NavRow
                icon={Database}
                title="Parser data"
                description="Browse spiders, sessions and stats"
                onClick={() => navigate('/settings/parser/view')}
                testId="settings-parser-view"
              />
              {isAdmin && (
                <NavRow
                  icon={RefreshCw}
                  title="Auto-import rules"
                  description="Recurring imports that run on a schedule"
                  onClick={() => navigate('/settings/parser/auto-import')}
                  testId="settings-auto-import"
                />
              )}
            </div>
          </div>
        </Card>

        {/* Background Jobs card --------------------------------------- */}
        {isAdmin && (
          <Card>
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-9 h-9 rounded bg-purpleColor/10 text-purpleColor flex-shrink-0">
                <Activity size={18} />
              </span>

              <div className="flex flex-col leading-tight min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-blackColor">
                    Background jobs
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-darkGrayColor">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${queueStatus.dotClass} ${
                        queueStatus.tone === 'blue' ||
                        queueStatus.tone === 'orange' ||
                        queueStatus.tone === 'red'
                          ? 'animate-pulse'
                          : ''
                      }`}
                      aria-label={`queue status: ${queueStatus.tone}`}
                    />
                    <span className="tabular-nums">{queueStatus.label}</span>
                  </span>
                </div>
                <span className="text-xs text-darkGrayColor mt-0.5">
                  Live view of queues, workers, and imports
                </span>
              </div>

              <button
                type="button"
                onClick={() => setIsQueueDashboardOpen(true)}
                data-testid="settings-open-queue-dashboard"
                className="flex items-center gap-1.5 text-sm font-medium text-white bg-purpleColor rounded px-3 py-1.5 hover:opacity-90 transition-opacity flex-shrink-0"
              >
                <span>Open dashboard</span>
                <ChevronRight size={14} />
              </button>
            </div>
          </Card>
        )}

        {/* About card -------------------------------------------------- */}
        <Card>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Info
                size={14}
                className="text-darkGrayColor"
              />
              <h2 className="text-xs uppercase tracking-wider text-darkGrayColor font-medium">
                About
              </h2>
            </div>

            <div className="flex items-center justify-between py-1">
              <span className="text-sm text-blackColor">Frontend</span>
              <VersionPill repo="Machingapp-front">{APP_VERSION}</VersionPill>
            </div>

            <div className="flex items-center justify-between py-1">
              <span className="text-sm text-blackColor">Backend</span>
              {isBackendVersionLoading ? (
                <SkeletonLine className="h-4 w-16" />
              ) : backendVersionError ? (
                <VersionPill>error</VersionPill>
              ) : (
                <VersionPill repo="Machingapp-back">
                  {backendVersionData?.version || 'N/A'}
                </VersionPill>
              )}
            </div>
          </div>
        </Card>

        <QueueDashboardModal
          isOpen={isQueueDashboardOpen}
          onClose={() => setIsQueueDashboardOpen(false)}
        />
      </div>
    </Container>
  );
};
